import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWebSocket } from '../../context/WebSocketContext';
import { Activity, TrendingUp, Trophy, Zap } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const activityIcons = {
    created: Zap,
    accepted: TrendingUp,
    resolved: Trophy,
};

const activityColors = {
    created: 'text-[#00FF66]',
    accepted: 'text-[#FFD700]',
    resolved: 'text-[#9B6FE8]',
};

export function LiveFeed() {
    const { t } = useTranslation();
    const { statsUpdate } = useWebSocket();
    const [activities, setActivities] = useState([]);
    const [isVisible, setIsVisible] = useState(true);

    // Fetch recent activity
    useEffect(() => {
        async function fetchActivity() {
            try {
                const response = await fetch(process.env.REACT_APP_BACKEND_URL + '/api/live/recent?limit=5');
                if (response.ok) {
                    const data = await response.json();
                    setActivities(data);
                }
            } catch (error) {
                console.error('Failed to fetch activity:', error);
            }
        }
        fetchActivity();
        
        // Refresh every 10 seconds
        const interval = setInterval(fetchActivity, 10000);
        return () => clearInterval(interval);
    }, []);

    if (activities.length === 0 || !isVisible) return null;

    return (
        <div className="bg-[#1a0a30]/50 border-b border-white/5 overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 py-2">
                <div className="flex items-center gap-6 overflow-x-auto scrollbar-hide relative">
                    {/* Live indicator */}
                    <div className="flex items-center gap-2 flex-shrink-0 bg-[#1a0a30] pr-4 relative z-10">
                        <div className="relative">
                            <div className="w-2 h-2 bg-red-500 rounded-full" />
                            <div className="absolute inset-0 w-2 h-2 bg-red-500 rounded-full animate-ping" />
                        </div>
                        <span className="text-xs font-semibold text-white/60 uppercase tracking-wider" data-testid="livefeed-live-label">{t('liveFeed.live')}</span>
                    </div>

                    {/* Activity items */}
                    <div className="flex items-center gap-6 animate-scroll">
                        {activities.map((activity, index) => {
                            const Icon = activityIcons[activity.type] || Activity;
                            const colorClass = activityColors[activity.type] || 'text-white';
                            
                            return (
                                <Link
                                    key={`${activity.bet_id}-${index}`}
                                    to={`/bet/${activity.bet_id}`}
                                    className="flex items-center gap-2 text-sm whitespace-nowrap hover:opacity-80 transition-opacity flex-shrink-0"
                                >
                                    <Icon className={`w-4 h-4 ${colorClass}`} />
                                    <span className="text-white/70">
                                        {activity.type === 'created' && t('liveFeed.newBet')}
                                        {activity.type === 'accepted' && t('liveFeed.betTaken')}
                                        {activity.type === 'resolved' && t('liveFeed.resolved')}
                                    </span>
                                    <span className="text-white font-medium max-w-[200px] truncate">
                                        {activity.description}
                                    </span>
                                    <span className={`font-mono ${colorClass}`}>
                                        {activity.amount?.toFixed(2)} {(activity.currency || 'ETH').toUpperCase()}
                                    </span>
                                </Link>
                            );
                        })}
                    </div>

                    {/* Stats */}
                    {statsUpdate && (
                        <div className="flex items-center gap-4 ml-auto flex-shrink-0 border-l border-white/10 pl-4">
                            <div className="text-xs">
                                <span className="text-white/40">{t('liveFeed.volume')}</span>
                                <span className="text-[#00FF66] font-mono ml-1">{statsUpdate.total_volume_eth?.toFixed(2)} ETH</span>
                            </div>
                            <div className="text-xs">
                                <span className="text-white/40">{t('liveFeed.fees')}</span>
                                <span className="text-[#FFD700] font-mono ml-1">{statsUpdate.total_fees_eth?.toFixed(4)} ETH</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                @keyframes scroll {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                }
                .animate-scroll {
                    animation: scroll 30s linear infinite;
                }
                .animate-scroll:hover {
                    animation-play-state: paused;
                }
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
                .scrollbar-hide {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>
        </div>
    );
}
